import {
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { deleteSymbols } from '$modules/personal/modules/batch';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { BatchesParseStorageService, BatchesService } from '../../services';
import {
  AddAttachmentBody,
  DepartureAttachmentItem,
  EditDepartureAttachmentParams,
} from '$modules/personal/modules/batch/features/batches/models';
import { ModalBaseDirective } from '$core/base/_modal.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { triggerFormValidation } from '$core/helpers/trigger-form-validation';
import { focusFirstInvalidField } from '$core/helpers/focus-first-invaild-field';
import { finalize, switchMap } from 'rxjs/operators';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { SwalService } from '$core/shell/swal/swal.service';

@Component({
  selector: 'app-add-attachment-modal',
  templateUrl: './add-attachment-modal.component.html',
  styleUrls: ['./add-attachment-modal.component.scss'],
})
export class AddAttachmentModalComponent
  extends ModalBaseDirective
  implements OnInit, OnDestroy
{
  private readonly swalService = inject(SwalService);

  public readonly deleteSymbols = deleteSymbols;
  @Input() public batchId: string;
  @Input() public batchDepartureId: string;
  @Input() public attachmentId: string;
  @Input() public isEditMode: boolean;
  @ViewChild('formEl', { static: false })
  public formEl: ElementRef<HTMLFormElement>;
  public attachmentForm: FormGroup;
  public isResponseLoading$ = new BehaviorSubject(false);
  public readonly departureBlankStatus$ =
    this.batchStorageService.departureBlankStatus$;
  public readonly batchDeparture$ = this.batchStorageService.batchDeparture$;
  public pattern = /^[А-яёЁa-zA-Z0-9!?@#$%^&*_\-+(){}\[\]<>\/|\\.,:~№ ]+$/;

  constructor(
    private readonly fb: FormBuilder,
    private readonly batchesService: BatchesService,
    protected modalRef: BsModalRef,
    protected modalService: BsModalService,
    protected translates: TranslateService,
    private readonly batchStorageService: BatchesParseStorageService
  ) {
    super(modalRef, modalService, translates);
  }

  public ngOnInit() {
    this.initForm();
    this.listenToForm();

    if (this.isEditMode) {
      this.attachmentForm.get('name').disable();
      this.attachmentForm.get('article').disable();
      this.attachmentForm.get('quantity').disable();
      this.attachmentForm.get('price').disable();
      this.getAttachmentById();
    }
  }

  private initForm() {
    this.attachmentForm = this.fb.group({
      article: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(20),
          Validators.pattern(this.pattern),
        ],
      ],
      quantity: [
        '',
        [
          Validators.required,
          Validators.min(1),
          Validators.max(999),
          Validators.pattern('^[0-9]+$'),
        ],
      ],
      price: [
        '',
        [
          Validators.required,
          Validators.min(0.01),
          Validators.max(99999.99),
          /*Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$')*/
        ],
      ],
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(60),
          Validators.pattern(this.pattern),
        ],
      ],
      total_price: [{ value: 0, disabled: true }, [Validators.required]],
    });
  }

  private getAttachmentById() {
    const body: EditDepartureAttachmentParams = {
      attachmentId: this.attachmentId,
      batchId: this.batchId,
      departureId: this.batchDepartureId,
    };

    this.isResponseLoading$.next(true);
    this.batchesService
      .getDepartureAttachmentById(body)
      .pipe(
        finalize(() => this.isResponseLoading$.next(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (attachment) => {
          this.fillForm(attachment);
          this.attachmentForm.get('name').enable();
          this.attachmentForm.get('article').enable();
          this.attachmentForm.get('quantity').enable();
          this.attachmentForm.get('price').enable();
        },
      });
  }

  private fillForm(attachment: DepartureAttachmentItem) {
    this.attachmentForm.patchValue({
      article: attachment.article,
      currency: attachment.currency,
      id: attachment.id,
      name: attachment.name,
      price: attachment.price,
      quantity: attachment.quantity,
      status: attachment.status,
      total_price: attachment.total_price,
    });
  }

  private listenToForm() {
    this.attachmentForm.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (res) => {
        if (
          res.price &&
          res.quantity &&
          this.attachmentForm.get('price').valid &&
          this.attachmentForm.get('quantity').valid
        ) {
          this.attachmentForm
            .get('total_price')
            .patchValue((res.price * res.quantity).toFixed(2), {
              emitEvent: false,
            });
        } else {
          this.attachmentForm
            .get('total_price')
            .patchValue(0, { emitEvent: false });
        }
      },
    });
  }

  public submit(isAddNext: boolean) {
    if (this.attachmentForm.invalid) {
      triggerFormValidation(this.attachmentForm);
      focusFirstInvalidField(this.formEl);
      return;
    }

    const body: AddAttachmentBody = {
      article: this.attachmentForm.get('article').value,
      name: this.attachmentForm.get('name').value,
      quantity: this.attachmentForm.get('quantity').value,
      price: this.attachmentForm.get('price').value,
    };

    const idParams: EditDepartureAttachmentParams = {
      attachmentId: this.attachmentId,
      batchId: this.batchId,
      departureId: this.batchDepartureId,
    };

    if (!this.isEditMode) {
      this.addAttachment(isAddNext, body);
    } else {
      this.editAttachment(idParams, body);
    }
  }

  private addAttachment(isAddNext: boolean, body: AddAttachmentBody) {
    this.isResponseLoading$.next(true);
    this.batchesService
      .addAttachment(this.batchId, this.batchDepartureId, body)
      .pipe(
        switchMap(() => {
          return forkJoin({
            batchDeparture: this.batchesService.showMailing({
              batchId: this.batchId,
              mailingId: this.batchDepartureId,
            }),
            batchDepartureAttachments:
              this.batchesService.getDepartureAttachments(
                this.batchId,
                this.batchDepartureId
              ),
          });
        }),
        finalize(() => {
          this.isResponseLoading$.next(false);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: ({ batchDeparture, batchDepartureAttachments }) => {
          this.batchStorageService.batchDeparture$.next(batchDeparture);
          this.batchStorageService.batchDepartureAttachments =
            batchDepartureAttachments;
          if (batchDeparture.document) {
            this.departureBlankStatus$.next(batchDeparture.document.status);
          }

          if (isAddNext && batchDepartureAttachments.length < 20) {
            this.initForm();
            this.listenToForm();
          } else {
            this.hide('success');
          }
        },
        error: (error) => this.onError(error),
      });
  }

  private editAttachment(
    idParams: EditDepartureAttachmentParams,
    body: AddAttachmentBody
  ) {
    this.isResponseLoading$.next(true);
    this.batchesService
      .editDepartureAttachment(idParams, body)
      .pipe(
        switchMap(() => {
          return forkJoin({
            attachments: this.batchesService.getDepartureAttachments(
              this.batchId,
              this.batchDepartureId
            ),
            batchDeparture: this.batchesService.showMailing({
              batchId: this.batchId,
              mailingId: this.batchDepartureId,
            }),
          });
        }),
        finalize(() => {
          this.isResponseLoading$.next(false);
          this.hide('success');
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: ({ attachments, batchDeparture }) => {
          this.batchStorageService.batchDepartureAttachments = attachments;
          this.batchStorageService.batchDeparture$.next(batchDeparture);

          if (batchDeparture.document) {
            this.departureBlankStatus$.next(batchDeparture.document.status);
          }
        },
        error: (error) => this.onError(error),
      });
  }

  public onLatinCyrillicDigitsKeyDown(
    event: KeyboardEvent,
    currentValue: string,
    maxLength: number
  ): void {
    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Shift',
      ].includes(event.key)
    ) {
      return;
    }

    if (
      currentValue &&
      String(currentValue).length >= maxLength &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete'
    ) {
      event.preventDefault();
      return;
    }

    if (!this.pattern.test(event.key)) {
      event.preventDefault();
    }
  }

  public onFractionalDigitKeydown(
    event: KeyboardEvent,
    currentValue: string,
    maxLength: number
  ): void {
    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Shift',
        ' ',
      ].includes(event.key)
    ) {
      return;
    }

    if (!/[0-9.\s]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const futureValue = currentValue + event.key;
    const [integerPart, decimalPart] = futureValue.split(/[.]/);

    if (futureValue.startsWith('.')) {
      event.preventDefault();
      return;
    }

    if (integerPart.length > maxLength) {
      event.preventDefault();
      return;
    }

    if (decimalPart && decimalPart.length > 2) {
      event.preventDefault();
    }

    if (event.key === '.' && currentValue.includes('.')) {
      event.preventDefault();
    }

    if (
      integerPart.startsWith('0') &&
      futureValue.length === 2 &&
      event.key !== '.'
    ) {
      event.preventDefault();
      return;
    }
  }

  public ngOnDestroy(): void {}

  private onError(error: HttpErrorResponse): void {
    const res = error;
    const swal = this.swalService.swal;
    swal.icon = 'error';
    swal.title = this.translateService.instant('AN_ERROR_HAS_OCCURRED');
    swal.text = res.error
      ? res.error.message || res.error.error
      : res.statusText;
    swal.fire();
  }
}
